import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { vacationsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { EmployeeVacationsSheet } from '../components/vacations/EmployeeVacationsSheet';
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function VacationBalance() {
  const { isAdmin, hasPermission } = useAuth();
  const canManage = isAdmin || hasPermission('vacations', 'edit') || hasPermission('vacations', 'execute');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await vacationsAPI.getAllBalances(year);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load vacation balances:', error);
      toast.error('Fehler beim Laden der Urlaubsdaten');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusColor = (remaining, total) => {
    const percentage = (remaining / total) * 100;
    if (percentage > 50) return 'text-green-600';
    if (percentage > 20) return 'text-amber-600';
    return 'text-red-600';
  };

  const getProgressColor = (remaining, total) => {
    const percentage = (remaining / total) * 100;
    if (percentage > 50) return 'bg-green-500';
    if (percentage > 20) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={`skel-${i}`} className="h-28 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="vacation-balance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Urlaubsübersicht</h1>
            <p className="text-muted-foreground mt-1">
              Resturlaub und Urlaubsplanung für {year}
            </p>
          </div>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[150px]" data-testid="year-select">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        {data?.summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                    <p className="text-2xl font-bold">{data.summary.total_employees}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Gesamtanspruch</p>
                    <p className="text-2xl font-bold">{data.summary.total_vacation_days} Tage</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <CheckCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Genommen</p>
                    <p className="text-2xl font-bold">{data.summary.total_used_days} Tage</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Clock className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Beantragt</p>
                    <p className="text-2xl font-bold">{data.summary.total_pending_days} Tage</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Employee Balance Table */}
        <Card className="border-border/60">
          <CardHeader className="border-b border-border/40">
            <CardTitle className="font-heading">Urlaubskonten der Mitarbeiter</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data?.balances?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="data-table">
                    <TableHead>Personal-Nr.</TableHead>
                    <TableHead>Mitarbeiter</TableHead>
                    <TableHead className="text-center">Jahresanspruch</TableHead>
                    <TableHead className="text-center">Übertrag</TableHead>
                    <TableHead className="text-center">Gesamt</TableHead>
                    <TableHead className="text-center">Genommen</TableHead>
                    <TableHead className="text-center">Beantragt</TableHead>
                    <TableHead className="text-center">Verfügbar</TableHead>
                    <TableHead className="w-[200px]">Status</TableHead>
                    {canManage && <TableHead className="w-[110px] text-right">Aktion</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.balances.map((balance, index) => {
                    const usedPercentage = (balance.used_days / balance.total_days) * 100;
                    const pendingPercentage = (balance.pending_days / balance.total_days) * 100;
                    
                    return (
                      <TableRow
                        key={balance.employee_id}
                        className="animate-slide-up"
                        style={{ animationDelay: `${index * 0.02}s` }}
                        data-testid={`balance-row-${balance.employee_id}`}
                      >
                        <TableCell className="font-mono text-sm">
                          {balance.employee_number}
                        </TableCell>
                        <TableCell className="font-medium">
                          {balance.employee_name}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{balance.base_days || balance.total_days}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {(balance.carryover || 0) > 0 ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              +{balance.carryover}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className="font-bold">{balance.total_days}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {balance.used_days}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {balance.pending_days > 0 ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              {balance.pending_days}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-bold ${getStatusColor(balance.available_days, balance.total_days)}`}>
                            {balance.available_days}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-secondary">
                              <div 
                                className="bg-green-500 transition-all" 
                                style={{ width: `${usedPercentage}%` }}
                              />
                              <div 
                                className="bg-amber-500 transition-all" 
                                style={{ width: `${pendingPercentage}%` }}
                              />
                            </div>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{Math.round(usedPercentage)}% genutzt</span>
                              <span>{balance.remaining_days} Rest</span>
                            </div>
                          </div>
                        </TableCell>
                        {canManage && (
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={() => { setSelectedEmployee(balance); setSheetOpen(true); }}
                              data-testid={`manage-vacations-${balance.employee_id}`}
                            >
                              <Settings2 className="h-3.5 w-3.5 mr-1" />
                              Verwalten
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg">Keine Daten verfügbar</h3>
                <p className="text-muted-foreground mt-1">
                  Es gibt keine aktiven Mitarbeiter für dieses Jahr.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Genommen</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span>Beantragt (ausstehend)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-secondary" />
            <span>Verfügbar</span>
          </div>
        </div>

        <EmployeeVacationsSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          employee={selectedEmployee}
          onChanged={loadData}
        />
      </div>
    </DashboardLayout>
  );
}
